import React, { useMemo } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';

// Power color - using a blue tone to distinguish from current (red tones)
const POWER_COLOR = '#1E90FF'; // DodgerBlue

const PowerTracking = ({ data }) => {
  const processedData = useMemo(() => {
    if (!data?.energycontrol) return [];

    // Check which data source is available
    const meteredData = data.energycontrol.power_metered || {};
    const habitaioData = data.energycontrol.power_habitaio || {};

    // Determine which dataset to use (prefer metered if available, otherwise habitaio)
    const sourceData = Object.keys(meteredData).length > 0 ? meteredData : habitaioData;

    if (Object.keys(sourceData).length === 0) return [];

    return Object.entries(sourceData).map(([date, values]) => ({
      date,
      max: values.max || 0,
      avg: values.avg || 0
    }));
  }, [data]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div className="custom-tooltip bg-white p-4 border rounded shadow-lg">
        <p className="font-bold">{label}</p>
        <table className="w-full mt-2">
          <tbody>
            {payload.map((entry, index) => (
              <tr key={index}>
                <td className="py-1">
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 mr-2"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span>{entry.name}</span>
                  </div>
                </td>
                <td className="text-right font-semibold">{entry.value.toFixed(1)} W</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!processedData.length) return null;

  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={processedData}
          margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            angle={-45}
            textAnchor="end"
            height={60}
          />

          <YAxis
            label={{ value: 'Power (W)', angle: -90, position: 'insideLeft' }}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend />

          {/* Max Power - Thicker solid line */}
          <Line
            type="monotone"
            dataKey="max"
            name="Max Power"
            stroke={POWER_COLOR}
            strokeWidth={2}
            dot={{ r: 4 }}
          />

          {/* Avg Power - Thinner dashed line */}
          <Line
            type="monotone"
            dataKey="avg"
            name="Avg Power"
            stroke={POWER_COLOR}
            strokeWidth={1}
            strokeDasharray="4 2"
            dot={{ r: 3 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PowerTracking;
